<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./ProductGroup.translations.json";
	import HeroBanner from "../components/HeroBanner.svelte";
	import ContactCards from "../components/ContactCards.svelte";
	import ProductBanner from "../components/ProductBanner.svelte";
	import type { ProductGroupProps } from "./ProductGroupProps";
	import CaseStudies from "../components/CaseStudies.svelte";
	import ProductGroups from "../components/ProductGroups.svelte";
	import carFooterBanner from "../assets/images/car-footer-banner.png?webp&w=1920&imagetools";
	import PartnersMap from "../components/PartnersMap.svelte";
	import { flyInOnScroll } from "../utils/flyInOnScroll.js";
	import ImageBanner from "../components/ImageBanner.svelte";
	import ProductGroupBannerCard from "../components/ProductGroupBannerCard.svelte";
	import LookInside from "../components/LookInside.svelte";
	import { getContext, setContext } from "svelte";
	import { productGroupsContextKey } from "../contexts/productGroupsContextKey.js";
	import type { ProductGroupWithTextifiedContent } from "../../../core/schema/ProductGroup.js";
	import Content from "../components/Content.svelte";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import InteractiveBrowser from "../components/InteractiveBrowser.svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { thumbForOgImage } from "../utils/thumbForOgImage.js";
	import { isFileImage } from "../../../core/utils/isFileImage.js";

	type $$Props = ProductGroupProps;

	const { STORAGE_URL } = getGlobalSettings();
	const { caseStudies, productGroup, productGroupBanner } = $$restProps as $$Props;

	const translate = getTranslate(translations);
	const productGroups = getContext<ProductGroupWithTextifiedContent[]>(productGroupsContextKey);
	setContext(
		thumbnailImageContextKey,
		thumbForOgImage(
			STORAGE_URL,
			productGroup.headerBanner && isFileImage(productGroup.headerBanner)
				? productGroup.headerBanner
				: (productGroup.headerBannerThumbnail ?? productGroup.primaryImage),
		),
	);
</script>

<Layout largeFooterImage footerImage={carFooterBanner} footerImageClass="max-lg:object-[80%_center]">
	<HeroBanner
		title={productGroup.name}
		titleClass="lg:text-5xl text-3xl lg:mt-0 mt-16"
		textColor="black"
		backgroundMedia={productGroup.headerBanner ?? productGroup.primaryImage}
		backgroundMediaMobile={productGroup.headerBannerMobile}
		videoPoster={productGroup.headerBannerThumbnail ?? productGroup.primaryImage}
		videoPosterMobile={productGroup.headerBannerThumbnail ?? productGroup.primaryImage}
		overlay="white-70"
		linkText={translate("showMore")}
		noLoopVideo
	/>
	<section class="container">
		<div
			use:flyInOnScroll
			class="group-content max-w-[70rem] pb-20 pt-[8.875rem] lg:w-9/12 lg:pb-[8.75rem] lg:pt-[12.875rem]"
			id="showMore"
		>
			<Content content={productGroup.content} customHeadlinesFontWeight={true} />
		</div>
	</section>
	{#if productGroupBanner}
		<section>
			<ImageBanner
				contentClass="!pb-[6.125rem]"
				title={productGroupBanner.title}
				subTitle={productGroupBanner.subtitle}
				uploaded={productGroupBanner.background}
				uploadedMobile={productGroupBanner.backgroundMobile}
			>
				<div class="absolute inset-0 -z-10 bg-gradient-to-t from-black/30 to-[#737373]/60"></div>
				<p use:flyInOnScroll class="text-secondary md:text-2md mt-auto max-w-[48.313rem] text-[1.125rem] font-light">
					{nbspify(productGroupBanner.content)}
				</p>
				<div slot="beside" class="flex w-full flex-col items-end gap-2 justify-self-end">
					{#each productGroupBanner.advantages as advantage}
						<ProductGroupBannerCard title={advantage.value} />
					{/each}
				</div>
			</ImageBanner>
		</section>
	{/if}
	<section class="bg-tertiary">
		<div class="container max-md:px-0">
			{#each productGroup.products as product}
				<ProductBanner {product} />
			{/each}
		</div>
	</section>
	<section class="bg-tertiary pt-16">
		<CaseStudies {caseStudies} />
	</section>
	<section class="mt-16">
		<InteractiveBrowser />
	</section>
	<section>
		<LookInside />
	</section>
	<section class="pt-[4.375rem] lg:pb-[11.563rem]">
		<PartnersMap />
	</section>
	<section>
		<ContactCards />
	</section>
	<section class="container">
		<div class="border-tertiary-300 border-b pb-20 pt-[7.5rem] lg:pt-[11.25rem]">
			<p class="text-2md">{translate("portfolioSubHeadline")}</p>
			<p class="text-xl font-semibold leading-[1.13] md:text-2xl lg:text-4xl">{translate("portfolioHeadline")}</p>
		</div>
		<ProductGroups {productGroups} topOffset={false} />
	</section>
</Layout>

<style lang="postcss" global>
	.group-content > .ckeditor-content {
		& > *:first-child {
			@apply mb-16 font-semibold sm:text-xl md:text-4xl md:leading-[1.135];
		}
	}
</style>
